import React, { useEffect, useState } from 'react';
import { Save, RefreshCw, Calculator, ArrowUp, ArrowDown } from 'lucide-react';
import { fetchItems, updateItems } from '../api/itemsApi';

const AdminBulkPrice = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [percentage, setPercentage] = useState(10);
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        loadProducts();
    }, []);

    const loadProducts = async () => {
        try {
            setLoading(true);
            const data = await fetchItems();
            setProducts(data);
            setHasChanges(false);
        } catch (error) {
            console.error('Error loading products:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePriceChange = (id, field, newPrice) => {
        let value = newPrice;
        if (typeof value === 'string') {
            value = value.replace(/\./g, '');
        }

        setProducts(prev => prev.map(p =>
            p.id === id ? { ...p, [field]: Number(value) } : p
        ));
        setHasChanges(true);
    };

    const applyPercentage = (increase) => {
        const factor = 1 + (percentage / 100) * (increase ? 1 : -1);
        setProducts(prev => prev.map(p => ({
            ...p,
            precio: Math.round((p.precio || 0) * factor),
            precioNegocio: Math.round((p.precioNegocio || 0) * factor),
            puntosRegulares: Math.round((p.puntosRegulares || 0) * factor),
            puntosEssenPlus: Math.round((p.puntosEssenPlus || 0) * factor),
            puntosXL: Math.round((p.puntosXL || 0) * factor)
        })));
        setHasChanges(true);
    };

    const handleSaveAll = async () => {
        if (!window.confirm(`¿Estás seguro de actualizar ${products.length} productos?`)) return;

        try {
            setSaving(true);
            const updates = products.map(p => ({
                ...p,
                id: p.id,
                precio: p.precio,
                precioNegocio: p.precioNegocio,
                puntosRegulares: p.puntosRegulares,
                puntosEssenPlus: p.puntosEssenPlus,
                puntosXL: p.puntosXL
            }));
            await updateItems(updates);
            setHasChanges(false);
            alert('Precios actualizados correctamente');
        } catch (error) {
            console.error('Error updating prices:', error);
            alert('Error al actualizar precios');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
            </div>
        );
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Actualización Masiva de Precios</h1>
                    <p className="text-gray-500 mt-1">Modifica precios individualmente o aplica porcentajes</p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={loadProducts}
                        disabled={saving}
                        className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
                    >
                        <RefreshCw className="w-5 h-5" />
                        Recargar
                    </button>
                    <button
                        onClick={handleSaveAll}
                        disabled={!hasChanges || saving}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Save className="w-5 h-5" />
                        {saving ? 'Guardando...' : 'Guardar Cambios'}
                    </button>
                </div>
            </div>

            {/* Tools Panel */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 mb-6">
                <div className="flex items-center gap-2 mb-4 text-purple-700 font-medium">
                    <Calculator className="w-5 h-5" />
                    <span>Herramientas de Cálculo</span>
                </div>
                <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-2">
                        <span className="text-gray-600">Porcentaje:</span>
                        <div className="relative">
                            <input
                                type="number"
                                value={percentage}
                                onChange={(e) => setPercentage(Number(e.target.value))}
                                className="w-20 px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 outline-none"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                        </div>
                    </div>
                    <button
                        onClick={() => applyPercentage(true)}
                        className="px-4 py-2 bg-green-50 text-green-700 hover:bg-green-100 rounded-lg transition-colors flex items-center gap-2 font-medium"
                    >
                        <ArrowUp className="w-4 h-4" />
                        Aumentar
                    </button>
                    <button
                        onClick={() => applyPercentage(false)}
                        className="px-4 py-2 bg-red-50 text-red-700 hover:bg-red-100 rounded-lg transition-colors flex items-center gap-2 font-medium"
                    >
                        <ArrowDown className="w-4 h-4" />
                        Disminuir
                    </button>
                </div>
            </div>

            {/* Products Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 text-gray-600 font-medium text-sm uppercase tracking-wider">
                            <tr>
                                <th className="px-6 py-4">Producto</th>
                                <th className="px-6 py-4">Precio Lista</th>
                                <th className="px-6 py-4">Precio Negocio</th>
                                <th className="px-6 py-4">Pts. Reg</th>
                                <th className="px-6 py-4">Pts. Essen+</th>
                                <th className="px-6 py-4">Pts. XL</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {products.map((product) => (
                                <tr key={product.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4">
                                        <div className="font-medium text-gray-900">{product.nombre}</div>
                                        <div className="text-sm text-gray-500">{product.linea}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="relative max-w-[120px]">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                                            <input
                                                type="number"
                                                value={product.precio}
                                                onChange={(e) => handlePriceChange(product.id, 'precio', e.target.value)}
                                                className="w-full pl-7 pr-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 outline-none"
                                            />
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="relative max-w-[120px]">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                                            <input
                                                type="number"
                                                value={product.precioNegocio || ''}
                                                onChange={(e) => handlePriceChange(product.id, 'precioNegocio', e.target.value)}
                                                className="w-full pl-7 pr-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 outline-none"
                                            />
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <input
                                            type="number"
                                            value={product.puntosRegulares || ''}
                                            onChange={(e) => handlePriceChange(product.id, 'puntosRegulares', e.target.value)}
                                            className="w-20 px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 outline-none"
                                        />
                                    </td>
                                    <td className="px-6 py-4">
                                        <input
                                            type="number"
                                            value={product.puntosEssenPlus || ''}
                                            onChange={(e) => handlePriceChange(product.id, 'puntosEssenPlus', e.target.value)}
                                            className="w-20 px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 outline-none"
                                        />
                                    </td>
                                    <td className="px-6 py-4">
                                        <input
                                            type="number"
                                            value={product.puntosXL || ''}
                                            onChange={(e) => handlePriceChange(product.id, 'puntosXL', e.target.value)}
                                            className="w-20 px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 outline-none"
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AdminBulkPrice;
